/* eslint-disable @typescript-eslint/no-explicit-any */
import { EStudentConnectionStatus, EStudentPaymentStatus } from '@constants';
import { IUserStatsFilters } from '@sdk/contracts';
import { Button, Checkbox, Collapse, DatePicker, Popover, Select } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CSSProperties } from 'styled-components';

import { Wrapper } from './FiltersHandler.styles';

import { Icon } from '@/components/Icon';
import { StudentConnectionStatusTag } from '@/components/StudentConnectionStatusTag';
import { StudentPaymentStatusTag } from '@/components/StudentPaymentStatusTag';
import { useTeachersList, useCourseStudentsStats, useCourseStats, useProfile } from '@/store';

const CHECKBOX_STYLE: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };

export const FiltersHandler = () => {
  const { t } = useTranslation(['UsersTranslations', 'DefaultTranslations']);
  const { filter, setFilter } = useCourseStudentsStats();
  const { courseStats } = useCourseStats();
  const { teachers } = useTeachersList({ fetchAll: true });
  const {
    roleCheck: { isManager }
  } = useProfile();

  const handleSetFilters = (key: keyof IUserStatsFilters, value: any) => {
    if (value === null) return setFilter({ ...filter, date: undefined });
    setFilter({ ...filter, [key]: value });
  };

  const tagsOptions = useMemo(() => {
    return courseStats?.course?.tags?.map(tag => ({
      value: tag._id,
      label: tag.name
    }));
  }, [courseStats]);

  const studentStatusOptions = useMemo(
    () =>
      Object.values(EStudentConnectionStatus).map(value => ({
        value,
        label: <StudentConnectionStatusTag userConnectionStatus={value} />
      })),
    [t]
  );

  const paymentStatusOptions = useMemo(
    () =>
      Object.values(EStudentPaymentStatus).map(value => ({
        value,
        label: <StudentPaymentStatusTag paymentStatus={value} />
      })),
    [t]
  );

  if (!isManager) return null;

  const content = (
    <Wrapper>
      <Collapse
        ghost
        bordered={false}
        expandIconPosition="end"
        expandIcon={({ isActive }) => <Icon i={isActive ? 'Subtract' : 'Add'} />}>
        <Collapse.Panel key="tags" header={t('TAGS')}>
          <Select
            options={tagsOptions}
            mode="multiple"
            allowClear
            filterOption={(input, option) =>
              (option?.label.toLocaleLowerCase() ?? '').includes(input.toLocaleLowerCase())
            }
            value={filter?.tags}
            onChange={value => handleSetFilters('tags', value as string[])}
          />
        </Collapse.Panel>
        <Collapse.Panel key="connectionStatus" header={t('DefaultTranslations:ACTIVITY_STATUS')}>
          <Checkbox.Group
            style={CHECKBOX_STYLE}
            options={studentStatusOptions}
            value={filter?.connectionStatus}
            onChange={value => handleSetFilters('connectionStatus', value)}
          />
        </Collapse.Panel>
        <Collapse.Panel key="paymentStatus" header={t('DefaultTranslations:PAYMENT_STATUS')}>
          <Checkbox.Group
            style={CHECKBOX_STYLE}
            options={paymentStatusOptions}
            value={filter?.paymentStatus}
            onChange={value => handleSetFilters('paymentStatus', value)}
          />
        </Collapse.Panel>
        <Collapse.Panel key="date" header={t('DATE_RANGE')}>
          <DatePicker.RangePicker
            format="DD/MM/YYYY"
            value={filter?.date ? [dayjs(filter?.date?.[0]), dayjs(filter?.date?.[1])] : undefined}
            onChange={value => handleSetFilters('date', value)}
          />
        </Collapse.Panel>
        <Collapse.Panel key="tutor" header={t('DefaultTranslations:TUTOR')}>
          <Select
            allowClear
            showSearch
            value={filter?.tutor}
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              (option?.label?.toLocaleLowerCase() ?? '').includes(input.toLocaleLowerCase())
            }
            placeholder={t('DefaultTranslations:SELECT_TUTOR')}
            options={teachers?.map(teacher => ({
              value: teacher._id,
              label: `${teacher.name} ${teacher.lastName}`
            }))}
            onChange={value => handleSetFilters('tutor', value)}
          />
        </Collapse.Panel>
      </Collapse>
    </Wrapper>
  );
  return (
    <Popover trigger={['click']} content={content} placement="bottomRight">
      <Button icon={<Icon i="Filter" />}>{t('FILTERS')}</Button>
    </Popover>
  );
};
